import React, { useEffect, useState } from 'react'
import ReactPaginate from 'react-paginate'
import { Helmet } from 'react-helmet'
import {
  TableCell,
  Table,
  TableRow,
  TableBody,
  TableHead,
  Paper,
  Button,
  Grid,
  DialogContent,
  DialogActions,
  Dialog,
  Typography,
  Tooltip,
  CircularProgress,
  FormControlLabel,
  Switch,
} from '@mui/material'
import { AddCircle, Edit, Clear } from '@mui/icons-material'
import { ReferralSourceContactDTO } from 'src/viewModels/ReferralSourceContactDTO'
import {
  getPagedReferralContacts,
  addReferralSourceContact,
  updateReferralSourceContact,
} from 'src/services/AdminService'
import { getReferralSourceContactTypeOptions } from 'src/services/CaseService'
import { pagedList } from 'src/viewModels/pagedList'
import ResponsiveAppBar from 'src/common/ResponsiveAppBar'
import * as Yup from 'yup'
import { Form, Formik } from 'formik'
import FullWidthField from 'src/common/FullWidthField'
import PhoneNumberMaskOBM from 'src/common/PhoneNumberMaskOBM'
import { ReferralSourceContactTypeOption } from 'src/viewModels/workflow/ReferralSourceContactTypeOption'
import SelectInput from 'src/common/SelectInput/SelectInput'

const ReferralContactPaper: React.FC = () => {
  const pathname = window.location.pathname
  const pathParts = pathname.split('/')
  const id = parseInt(pathParts[pathParts.length - 1], 10)
  const [gridData, setGridData] = useState<ReferralSourceContactDTO[]>(
    new pagedList().items
  )
  const [referralSourceContactTypeOption, setReferralSourceContactTypeOption] = useState<
    ReferralSourceContactTypeOption[]
  >([])
  const [selectedContact, setSelectedContact] = useState<any>(null)
  const [gridLoading, setGridLoading] = useState(false)
  const [openAddModal, setOpenAddModal] = useState(false)
  const [openEditModal, setOpenEditModal] = useState(false)
  const [, setNewId] = useState<string>('')

  const [totalItems, setTotalItems] = useState(0)
  const [firstRecordIndex, setFirstRecordIndex] = useState(0)
  const [lastRecordIndex, setLastRecordIndex] = useState(0)

  const [pageCount, setPageCount] = useState(0)
  const [page, setPage] = useState(1)
  const [pageSize] = useState(10)

  const validationSchema = Yup.object().shape({
    rsId: Yup.string().notRequired(),
    newRsContactId: Yup.string().notRequired(),
    newRsContactFirstName: Yup.string().required('First name is required'),
    newRsContactLastName: Yup.string().required('Last name is required'),
    newRsContactTypeId: Yup.string().notRequired(),
    newRsContactPhone: Yup.string().notRequired(),
    newRsContactFax: Yup.string().notRequired(),
    newRsContactEmail: Yup.string().email('Invalid email').notRequired(),
    newRsContactComments: Yup.string().notRequired(),
    inActive: Yup.boolean().notRequired(),
    referralSource: Yup.object().notRequired(),
  })

  const handleAddClick = () => {
    setOpenAddModal(true)
  }

  const handleCloseAddModal = () => {
    setOpenAddModal(false)
  }

  const handleCloseEditModal = () => {
    setOpenEditModal(false)
  }

  const handleEditClick = (contactId: string) => {
    const contact = gridData.find((c) => c.rsContactId === contactId)
    setSelectedContact(contact)
    setOpenEditModal(true)
    setNewId(contactId ?? '')
  }

  const handleAddSave = async (values: any, { setSubmitting }: any) => {
    setGridLoading(true)
    try {
      const newReferralSource: ReferralSourceContactDTO = {
        rsId: id.toString(),
        rsContactId: values.newRsContactId,
        rsContactFirstName: values.newRsContactFirstName,
        rsContactLastName: values.newRsContactLastName,
        rsContactTypeId: values.newRsContactTypeId,
        rsContactPhone: values.newRsContactPhone,
        rsContactFax: values.newRsContactFax,
        rsContactEmail: values.newRsContactEmail,
        rsContactComments: values.newRsContactComments,
        inActive: values.inActive,
      }
      await addReferralSourceContact(newReferralSource)
      handleCloseAddModal()
      loadContacts()
    } finally {
      setGridLoading(false)
      setSubmitting(false)
    }
  }

  const handleEditSave = async (values: any, { setSubmitting }: any) => {
    setGridLoading(true)
    try {
      const newReferralSource: ReferralSourceContactDTO = {
        rsId: id.toString(),
        rsContactId: values.newRsContactId,
        rsContactFirstName: values.newRsContactFirstName,
        rsContactLastName: values.newRsContactLastName,
        rsContactTypeId: values.newRsContactTypeId,
        rsContactPhone: values.newRsContactPhone,
        rsContactFax: values.newRsContactFax,
        rsContactEmail: values.newRsContactEmail,
        rsContactComments: values.newRsContactComments,
        inActive: values.inActive,
      }
      await updateReferralSourceContact(values.newRsContactId, newReferralSource)
      handleCloseEditModal()
      loadContacts()
    } finally {
      setGridLoading(false)
      setSubmitting(false)
    }
  }

  const setPaginationOffsetData = (totalItems: number) => {
    const firstIndex = (page - 1) * pageSize + 1
    const lastIndex = page * pageSize < totalItems ? page * pageSize : totalItems
    setFirstRecordIndex(firstIndex)
    setLastRecordIndex(lastIndex)
  }

  const handlePageChange = (event: any) => {
    const newPage = event.selected + 1
    setPage(newPage)
    getPagedReferralContacts(id, pageCount, pageSize)
  }

  const paginationInfo = () => {
    if (!isSearchGridDataEmpty()) {
      return (
        <div className="pagination">
          <Typography variant="caption" gutterBottom>
            Showing {firstRecordIndex} to {lastRecordIndex} of {totalItems} entries
          </Typography>
        </div>
      )
    }
    return <></>
  }

  const loadContacts = async () => {
    setGridLoading(true)

    const response = await getPagedReferralContacts(id, page, pageSize)

    const { items, totalItems, totalPages } = response

    setTotalItems(totalItems)
    setPageCount(totalPages)
    setGridData(
      items.map((item: any) => ({
        rsId: item.rsId,
        rsContactId: item.rsContactId,
        rsContactFirstName: item.rsContactFirstName,
        rsContactLastName: item.rsContactLastName,
        rsContactTypeId: item.rsContactTypeId,
        rsContactPhone: item.rsContactPhone,
        rsContactFax: item.rsContactFax,
        rsContactEmail: item.rsContactEmail,
        rsContactComments: item.rsContactComments,
        inActive: item.inActive,
        referralSource: item.referralSource,
      }))
    )

    if (totalItems > 0) {
      setPaginationOffsetData(totalItems)
    }
    setGridLoading(false)
  }

  const isSearchGridDataEmpty = () => {
    return !gridData || gridData.length === 0
  }

  const mapSearchGridData = () => {
    if (isSearchGridDataEmpty()) {
      return (
        <TableRow className="gridNoPadding" key={1}>
          <TableCell colSpan={4} align="center">
            No records found
          </TableCell>
        </TableRow>
      )
    }

    return gridData.map((item, index) => (
      <TableRow className="gridPadding" key={index}>
        <TableCell>
          {item.rsContactLastName}, {item.rsContactFirstName}
        </TableCell>
        <TableCell>{item.rsContactEmail}</TableCell>
        <TableCell>
          {item.inActive ? (
            <Clear style={{ color: 'red', verticalAlign: 'middle' }} />
          ) : null}
        </TableCell>
        <TableCell style={{ textAlign: 'right' }}>
          <Tooltip title="Edit Contact" placement="top">
            <Edit
              color="primary"
              style={{ fontSize: '20px', cursor: 'pointer' }}
              onClick={() => handleEditClick(item.rsContactId ?? '')}
            />
          </Tooltip>
        </TableCell>
      </TableRow>
    ))
  }

  useEffect(() => {
    const fetchContactTypes = async () => {
      const contactTypes = await getReferralSourceContactTypeOptions()
      setReferralSourceContactTypeOption(contactTypes)
    }
    fetchContactTypes()
    loadContacts()
  }, [page, pageSize])

  return (
    <>
      <Helmet>
        <title>Referral Contact</title>
      </Helmet>
      {gridLoading && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            backgroundColor: 'rgba(0,0,0,0.3)',
            zIndex: 9999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <CircularProgress size={100} />
        </div>
      )}
      <Paper
        style={{
          width: '97%',
          margin: '20px',
          height: '100%',
          paddingBottom: '60px',
          position: 'relative',
        }}
      >
        <Grid
          container
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          style={{ padding: '15px 20px 0 20px' }}
        >
          <Grid item>
            <Typography
              variant="h6"
              color="textSecondary"
              style={{
                fontSize: '18px',
                fontWeight: 500,
                letterSpacing: '1px',
              }}
            >
              REFERRAL CONTACTS
            </Typography>
          </Grid>
          <Grid item>
            <Tooltip title="Add New Contact" placement="top">
              <AddCircle
                color="primary"
                style={{ fontSize: '30px', cursor: 'pointer' }}
                onClick={handleAddClick}
              />
            </Tooltip>
          </Grid>
        </Grid>
        <Grid
          item
          container
          direction="row"
          justifyContent="space-between"
          alignContent="center"
          alignItems="flex-start"
          wrap="wrap"
        >
          <Grid item xs={12}>
            <Table aria-label="simple table" style={{ padding: '20px' }}>
              <TableHead>
                <TableRow className="gridPadding">
                  <TableCell
                    style={{
                      width: '33.33%',
                      paddingRight: '160px',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    Contact Name
                  </TableCell>
                  <TableCell style={{ width: '33.33%' }}>Email</TableCell>
                  <TableCell style={{ width: '33.33%' }} align="left">
                    Inactive
                  </TableCell>
                  <TableCell style={{}}></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>{mapSearchGridData()}</TableBody>
            </Table>
          </Grid>
        </Grid>
        <div
          style={{
            position: 'absolute',
            bottom: 16,
            right: 32,
            display: 'flex',
            alignItems: 'center',
            zIndex: 1,
          }}
        >
          {paginationInfo()}
          <ReactPaginate
            previousLabel={'<'}
            nextLabel={'>'}
            onPageChange={handlePageChange}
            forcePage={page - 1}
            pageCount={pageCount}
            containerClassName={'pagination'}
            activeClassName={'active'}
            renderOnZeroPageCount={() => null}
          />
        </div>
      </Paper>
      <Dialog
        open={openAddModal}
        onClose={handleCloseAddModal}
        disableEscapeKeyDown
        maxWidth="md"
        fullWidth
      >
        <ResponsiveAppBar title="Add Contact" isModal={true}></ResponsiveAppBar>
        <Formik
          initialValues={{
            rsId: '',
            newRsContactId: '',
            newRsContactFirstName: '',
            newRsContactLastName: '',
            newRsContactTypeId: '',
            newRsContactPhone: '',
            newRsContactFax: '',
            newRsContactEmail: '',
            newRsContactComments: '',
            inActive: false,
            referralSource: { rsId: id?.toString() },
          }}
          validationSchema={validationSchema}
          onSubmit={handleAddSave}
        >
          {({ isSubmitting, isValid, values, setFieldValue }) => (
            <Form>
              <Typography
                variant={'h6'}
                color="textSecondary"
                style={{
                  height: '18px',
                  paddingLeft: '20px',
                  fontSize: '18px',
                  paddingBottom: '20px',
                }}
              >
                CONTACT DETAILS
              </Typography>
              <Grid container xs={12} direction="row">
                <Grid item xs={4}>
                  <DialogContent style={{ padding: '8px 16px' }}>
                    <FullWidthField
                      name="newRsContactFirstName"
                      label="First Name"
                      variant="outlined"
                      margin="dense"
                      fullWidth
                      required
                      inputProps={{ maxLength: 50 }}
                      InputLabelProps={{ shrink: true }}
                    />
                  </DialogContent>
                </Grid>
                <Grid item xs={4}>
                  <DialogContent style={{ padding: '8px 16px' }}>
                    <FullWidthField
                      name="newRsContactLastName"
                      label="Last Name"
                      variant="outlined"
                      margin="dense"
                      fullWidth
                      required
                      inputProps={{ maxLength: 50 }}
                      InputLabelProps={{ shrink: true }}
                    />
                  </DialogContent>
                </Grid>
                <Grid item xs={4}>
                  <DialogContent style={{ padding: '17px 16px' }}>
                    <SelectInput
                      inputId="newRsContactTypeId"
                      getName={(item: ReferralSourceContactTypeOption) =>
                        item.description
                      }
                      getValue={(item: ReferralSourceContactTypeOption) => item.id}
                      label="Contact Type"
                      name="newRsContactTypeId"
                      shrinkLabel={true}
                      fullWidth
                      outlined={true}
                      multiple={false}
                      native={true}
                      items={referralSourceContactTypeOption}
                      value={values.newRsContactTypeId}
                      onChange={(event) => {
                        setFieldValue('newRsContactTypeId', event.target.value)
                      }}
                    />
                  </DialogContent>
                </Grid>
                <Grid item xs={6}></Grid>
              </Grid>
              <Grid container direction="row">
                <Grid container item xs={12} direction="row">
                  <Grid item xs={4}>
                    <DialogContent style={{ padding: '8px 16px' }}>
                      <FullWidthField
                        name="newRsContactEmail"
                        label="Contact Email"
                        variant="outlined"
                        margin="dense"
                        fullWidth
                        inputProps={{ maxLength: 50 }}
                        InputLabelProps={{ shrink: true }}
                      />
                    </DialogContent>
                  </Grid>
                  <Grid item xs={4}>
                    <DialogContent style={{ padding: '8px 16px' }}>
                      <FullWidthField
                        name="newRsContactPhone"
                        label="Contact Phone"
                        variant="outlined"
                        margin="dense"
                        fullWidth
                        InputProps={{
                          inputComponent: PhoneNumberMaskOBM,
                          maxLength: 10,
                        }}
                        InputLabelProps={{ shrink: true }}
                      />
                    </DialogContent>
                  </Grid>
                  <Grid item xs={4}>
                    <DialogContent style={{ padding: '8px 16px' }}>
                      <FullWidthField
                        name="newRsContactFax"
                        label="Contact Fax"
                        variant="outlined"
                        margin="dense"
                        fullWidth
                        InputProps={{
                          inputComponent: PhoneNumberMaskOBM,
                          maxLength: 10,
                        }}
                        InputLabelProps={{ shrink: true }}
                      />
                    </DialogContent>
                  </Grid>
                </Grid>
                <Grid container direction="row">
                  <Typography
                    variant={'h6'}
                    color="textSecondary"
                    style={{
                      height: '18px',
                      paddingLeft: '20px',
                      fontSize: '18px',
                      paddingBottom: '20px',
                    }}
                  >
                    COMMENTS
                  </Typography>
                  <Grid container item xs={12} direction="row">
                    <Grid item xs={12}>
                      <DialogContent style={{ padding: '8px 16px', maxWidth: '100%' }}>
                        <FullWidthField
                          name="newRsContactComments"
                          variant="outlined"
                          margin="dense"
                          fullWidth
                          multiline
                          minRows={4}
                          inputProps={{ maxLength: 500 }}
                          InputLabelProps={{ shrink: true }}
                          style={{ width: '100%' }}
                        />
                      </DialogContent>
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
              <DialogActions>
                <Button
                  onClick={handleCloseAddModal}
                  color="primary"
                  disabled={isSubmitting || gridLoading}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  color="primary"
                  variant="contained"
                  disabled={isSubmitting || !isValid || gridLoading}
                >
                  Save
                </Button>
              </DialogActions>
            </Form>
          )}
        </Formik>
      </Dialog>
      <Dialog
        open={openEditModal}
        onClose={handleCloseAddModal}
        disableEscapeKeyDown
        maxWidth="md"
        fullWidth
      >
        <ResponsiveAppBar title="Edit Contact" isModal={true}></ResponsiveAppBar>
        <Formik
          enableReinitialize
          initialValues={{
            rsId: selectedContact?.rsId || '',
            newRsContactId: selectedContact?.rsContactId || '',
            newRsContactFirstName: selectedContact?.rsContactFirstName || '',
            newRsContactLastName: selectedContact?.rsContactLastName || '',
            newRsContactTypeId: selectedContact?.rsContactTypeId || '',
            newRsContactPhone: selectedContact?.rsContactPhone || '',
            newRsContactFax: selectedContact?.rsContactFax || '',
            newRsContactEmail: selectedContact?.rsContactEmail || '',
            newRsContactComments: selectedContact?.rsContactComments || '',
            inActive: selectedContact?.inActive || false,
            referralSource: selectedContact?.referralSource || { rsId: id?.toString() },
          }}
          validationSchema={validationSchema}
          onSubmit={handleEditSave}
        >
          {({ isSubmitting, isValid, values, setFieldValue }) => (
            <Form>
              <Typography
                variant={'h6'}
                color="textSecondary"
                style={{
                  height: '18px',
                  paddingLeft: '20px',
                  fontSize: '18px',
                  paddingBottom: '20px',
                }}
              >
                CONTACT DETAILS
              </Typography>
              <Grid container xs={12} direction="row">
                <Grid item xs={4}>
                  <DialogContent style={{ padding: '8px 16px' }}>
                    <FullWidthField
                      name="newRsContactFirstName"
                      label="First Name"
                      variant="outlined"
                      margin="dense"
                      fullWidth
                      required
                      inputProps={{ maxLength: 50 }}
                      InputLabelProps={{ shrink: true }}
                    />
                  </DialogContent>
                </Grid>
                <Grid item xs={4}>
                  <DialogContent style={{ padding: '8px 16px' }}>
                    <FullWidthField
                      name="newRsContactLastName"
                      label="Last Name"
                      variant="outlined"
                      margin="dense"
                      fullWidth
                      required
                      inputProps={{ maxLength: 50 }}
                      InputLabelProps={{ shrink: true }}
                    />
                  </DialogContent>
                </Grid>
                <Grid item xs={4}>
                  <DialogContent style={{ padding: '17px 16px' }}>
                    <SelectInput
                      inputId="newRsContactTypeId"
                      getName={(item: ReferralSourceContactTypeOption) =>
                        item.description
                      }
                      getValue={(item: ReferralSourceContactTypeOption) => item.id}
                      label="Contact Type"
                      name="newRsContactTypeId"
                      shrinkLabel={true}
                      fullWidth
                      outlined={true}
                      multiple={false}
                      native={true}
                      items={referralSourceContactTypeOption}
                      value={values.newRsContactTypeId}
                      onChange={(event) => {
                        setFieldValue('newRsContactTypeId', event.target.value)
                      }}
                    />
                  </DialogContent>
                </Grid>
                <Grid item xs={6}></Grid>
              </Grid>
              <Grid container direction="row">
                <Grid container item xs={12} direction="row">
                  <Grid item xs={4}>
                    <DialogContent style={{ padding: '8px 16px' }}>
                      <FullWidthField
                        name="newRsContactEmail"
                        label="Contact Email"
                        variant="outlined"
                        margin="dense"
                        fullWidth
                        inputProps={{ maxLength: 50 }}
                        InputLabelProps={{ shrink: true }}
                      />
                    </DialogContent>
                  </Grid>
                  <Grid item xs={4}>
                    <DialogContent style={{ padding: '8px 16px' }}>
                      <FullWidthField
                        name="newRsContactPhone"
                        label="Contact Phone"
                        variant="outlined"
                        margin="dense"
                        fullWidth
                        InputProps={{
                          inputComponent: PhoneNumberMaskOBM,
                          maxLength: 10,
                        }}
                        InputLabelProps={{ shrink: true }}
                      />
                    </DialogContent>
                  </Grid>
                  <Grid item xs={4}>
                    <DialogContent style={{ padding: '8px 16px' }}>
                      <FullWidthField
                        name="newRsContactFax"
                        label="Contact Fax"
                        variant="outlined"
                        margin="dense"
                        fullWidth
                        InputProps={{
                          inputComponent: PhoneNumberMaskOBM,
                          maxLength: 10,
                        }}
                        InputLabelProps={{ shrink: true }}
                      />
                    </DialogContent>
                  </Grid>
                </Grid>
                <Grid container direction="row">
                  <Typography
                    variant={'h6'}
                    color="textSecondary"
                    style={{
                      height: '18px',
                      paddingLeft: '20px',
                      fontSize: '18px',
                      paddingBottom: '20px',
                    }}
                  >
                    COMMENTS
                  </Typography>
                  <Grid container item xs={12} direction="row">
                    <Grid item xs={12}>
                      <DialogContent style={{ padding: '8px 16px', maxWidth: '100%' }}>
                        <FullWidthField
                          name="newRsContactComments"
                          variant="outlined"
                          margin="dense"
                          fullWidth
                          multiline
                          minRows={4}
                          inputProps={{ maxLength: 500 }}
                          InputLabelProps={{ shrink: true }}
                          style={{ width: '100%' }}
                        />
                      </DialogContent>
                    </Grid>
                    <Grid item>
                      <DialogContent style={{ padding: '8px 16px' }}>
                        <FormControlLabel
                          control={
                            <Switch
                              name="inActive"
                              color="primary"
                              checked={values.inActive}
                              onChange={(event) => {
                                setFieldValue('inActive', event.target.checked)
                              }}
                            />
                          }
                          label={values.inActive ? 'Inactive' : 'Active'}
                          labelPlacement="end"
                        />
                      </DialogContent>
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
              <DialogActions>
                <Button onClick={handleCloseEditModal} color="primary">
                  Cancel
                </Button>
                <Button
                  type="submit"
                  color="primary"
                  variant="contained"
                  disabled={isSubmitting || !isValid}
                >
                  Save
                </Button>
              </DialogActions>
            </Form>
          )}
        </Formik>
      </Dialog>
    </>
  )
}

export default ReferralContactPaper
